import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from "jwt-decode";
import Notificaciones from './Notificaciones';
import MenuLateral from './MenuLateral';
import { FiChevronDown, FiLogOut, FiGrid, FiPlusCircle } from 'react-icons/fi';
import { IMAGE_DOMAIN } from '../config/config';
import SelectorIdioma from './ui/SelectorIdioma';

const Header = ({ authenticated }) => {
    const token = localStorage.getItem("AuthToken");
    const location = useLocation();
    const { t } = useTranslation();

    let tokenInfo = {};
    if (token) {
        tokenInfo = jwtDecode(token);
    }

    const userRole = tokenInfo.role || null;
    const estadoBloqueado = tokenInfo.estado === "verificar" || tokenInfo.estado === "suspendido";

    const displayName = [tokenInfo.nombre, tokenInfo.apellidos].filter(Boolean).join(' ') || tokenInfo.usuario || t('comun.usuario');
    const avatarUrl = tokenInfo.fotoPerfil
        ? (tokenInfo.fotoPerfil.startsWith('http') ? tokenInfo.fotoPerfil : `${IMAGE_DOMAIN}${tokenInfo.fotoPerfil}`)
        : `${IMAGE_DOMAIN}/images/usuario.png`;

    const [isScrolled, setIsScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openMobileGroups, setOpenMobileGroups] = useState({});
    const dropdownRef = useRef(null);
    const profileRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Cerrar dropdowns de escritorio al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setActiveDropdown(null);
            }
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Cerrar dropdowns y menú móvil al cambiar de ruta
    useEffect(() => {
        setActiveDropdown(null);
        setIsProfileOpen(false);
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    // Cerrar menú móvil/perfil con la tecla Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsMobileMenuOpen(false);
                setIsProfileOpen(false);
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, []);

    // Bloquear el scroll del body mientras el menú móvil está abierto
    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isMobileMenuOpen]);

    const handleLinkClick = () => {
        window.scrollTo({ top: 0 });
        setActiveDropdown(null);
        setIsProfileOpen(false);
        setIsMobileMenuOpen(false);
    };

    const handleLogout = () => {
        localStorage.removeItem("AuthToken");
        window.location.reload();
    };

    const toggleDropdown = (name) => {
        setActiveDropdown(prev => prev === name ? null : name);
    };

    const toggleMobileGroup = (name) => {
        setOpenMobileGroups(prev => ({ ...prev, [name]: !prev[name] }));
    };

    // ── Definición de menús por rol ──────────────────────────────────────────
    // Las entradas guardan la clave de traducción; el texto se resuelve al pintar
    const menuUsuario = [
        { clave: 'menu.misTickets', path: '/dashboard' },
        { clave: 'menu.nuevoTicket', path: '/nuevo-ticket' },
    ];

    const menuTecnico = [
        { clave: 'menu.gestionTickets', path: '/dashboard/tickets' },
        { clave: 'menu.gestionUsuarios', path: '/dashboard/usuarios' },
    ];

    const menuAdmin = [
        {
            id: 'gestion',
            clave: 'menu.gestion',
            submenu: [
                { clave: 'menu.tickets', path: '/dashboard/tickets' },
                { clave: 'menu.usuarios', path: '/dashboard/usuarios' },
                { clave: 'menu.proyectos', path: '/dashboard/gestion-proyectos' },
                { clave: 'menu.notificaciones', path: '/dashboard/notificaciones' },
            ]
        },
        {
            id: 'material',
            clave: 'menu.material',
            submenu: [
                { clave: 'menu.equipos', path: '/dashboard/equipos' },
                { clave: 'menu.perifericos', path: '/dashboard/perifericos' },
            ]
        },
        { clave: 'menu.ajustesSistema', path: '/dashboard/ajustes-del-sistema' },
    ];

    const isActive = (path) => location.pathname === path;
    const isDropdownActive = (items) => items.some(i => location.pathname === i.path);

    // Sesión activa y cuenta operativa: el menú responsive es el mismo panel
    // lateral del dashboard. Invitados y cuentas bloqueadas siguen con el
    // menú simple (login/registro, salir).
    const usarMenuLateral = authenticated && !estadoBloqueado;

    // ── Render menú central desktop ─────────────────────────────────────────
    const renderDesktopNav = () => {
        if (!authenticated || estadoBloqueado) return null;

        if (userRole === 'admin') {
            return (
                <nav className="header-nav" ref={dropdownRef}>
                    {menuAdmin.map((group) => {
                        if (!group.submenu) {
                            return (
                                <Link
                                    key={group.path}
                                    to={group.path}
                                    className={`nav-item nav-link ${isActive(group.path) ? 'active' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    {t(group.clave)}
                                </Link>
                            );
                        }
                        return (
                            <div
                                key={group.id}
                                className={`nav-item has-dropdown ${activeDropdown === group.id ? 'open' : ''} ${isDropdownActive(group.submenu) ? 'active' : ''}`}
                            >
                                <button
                                    className="nav-btn"
                                    onClick={() => toggleDropdown(group.id)}
                                    aria-expanded={activeDropdown === group.id}
                                >
                                    {t(group.clave)}
                                    <FiChevronDown className="nav-chevron" />
                                </button>
                                <div className="nav-dropdown">
                                    {group.submenu.map((item) => (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className={`nav-dropdown-item ${isActive(item.path) ? 'active' : ''}`}
                                            onClick={handleLinkClick}
                                        >
                                            {t(item.clave)}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </nav>
            );
        }

        const menu = userRole === 'tecnico' ? menuTecnico : menuUsuario;
        return (
            <nav className="header-nav">
                {menu.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`nav-item nav-link ${isActive(item.path) ? 'active' : ''}`}
                        onClick={handleLinkClick}
                    >
                        {t(item.clave)}
                    </Link>
                ))}
            </nav>
        );
    };

    // ── Render menú lateral (móvil) a partir de los mismos datos ────────────
    const renderMobileNav = () => {
        if (!authenticated || estadoBloqueado) return null;

        if (userRole === 'admin') {
            return (
                <div className="mobile-nav">
                    {menuAdmin.map((group) => {
                        if (!group.submenu) {
                            return (
                                <Link
                                    key={group.path}
                                    to={group.path}
                                    className={`mobile-nav-link ${isActive(group.path) ? 'active' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    {t(group.clave)}
                                </Link>
                            );
                        }
                        return (
                            <div className="mobile-nav-group" key={group.id}>
                                <button
                                    className={`mobile-nav-group-btn ${openMobileGroups[group.id] ? 'open' : ''}`}
                                    onClick={() => toggleMobileGroup(group.id)}
                                    aria-expanded={!!openMobileGroups[group.id]}
                                >
                                    {t(group.clave)}
                                    <FiChevronDown className="nav-chevron" />
                                </button>
                                {openMobileGroups[group.id] && (
                                    <div className="mobile-nav-submenu">
                                        {group.submenu.map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className={`mobile-nav-sublink ${isActive(item.path) ? 'active' : ''}`}
                                                onClick={handleLinkClick}
                                            >
                                                {t(item.clave)}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }

        const menu = userRole === 'tecnico' ? menuTecnico : menuUsuario;
        return (
            <div className="mobile-nav">
                {menu.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                        onClick={handleLinkClick}
                    >
                        {t(item.clave)}
                    </Link>
                ))}
            </div>
        );
    };

    return (
        <>
        <header className={isScrolled ? 'scrolled' : ''}>
            <div className='site-width grid-header'>
                <Link to="/" className='logo-link' onClick={handleLinkClick}>
                    <img className='header-logo' src='/images/logoadhoc.svg' alt='Logo ADHOC'/>
                </Link>

                {/* Menú central con subapartados por rol */}
                {renderDesktopNav()}

                <div className="user-actions">
                    {authenticated ? (
                        <>
                            {!estadoBloqueado && <Notificaciones />}
                            <div className="profile-card" ref={profileRef}>
                                <button
                                    type="button"
                                    className="profile-trigger"
                                    onClick={() => setIsProfileOpen(prev => !prev)}
                                    aria-expanded={isProfileOpen}
                                >
                                    <img className="profile-avatar" src={avatarUrl} alt="" />
                                    <span className="profile-name">{displayName}</span>
                                    <FiChevronDown className="nav-chevron" />
                                </button>
                                <div className={`profile-dropdown ${isProfileOpen ? 'open' : ''}`}>
                                    {!estadoBloqueado && (
                                        <Link to="/nuevo-ticket" className="profile-dropdown-item" onClick={handleLinkClick}>
                                            <FiPlusCircle /> {t('menu.crearTicket')}
                                        </Link>
                                    )}
                                    <Link to="/dashboard" className="profile-dropdown-item" onClick={handleLinkClick}>
                                        <FiGrid /> {t('menu.dashboard')}
                                    </Link>
                                    <hr className="profile-dropdown-separator" />
                                    <button className="profile-dropdown-item profile-dropdown-logout" onClick={handleLogout}>
                                        <FiLogOut /> {t('menu.cerrarSesion')}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <span className="header-selector-idioma"><SelectorIdioma /></span>
                            <Link to="/iniciar-sesion" className='login' onClick={handleLinkClick}>
                                {t('menu.iniciarSesion')}
                            </Link>
                            <Link to="/registrarse" className='new-user' onClick={handleLinkClick}>
                                {t('menu.crearCuenta')}
                            </Link>
                        </>
                    )}
                </div>

                {/* Burger responsive */}
                <div className="grid-burger">
                    {authenticated && !estadoBloqueado ? (<Notificaciones />) : ''}
                    <div className="adhoc-burger">
                        <button
                            type="button"
                            className={`menu-icon ${isMobileMenuOpen ? 'open' : ''}`}
                            onClick={() => setIsMobileMenuOpen(prev => !prev)}
                            aria-label={isMobileMenuOpen ? t('comun.cerrarMenu') : t('comun.abrirMenu')}
                            aria-expanded={isMobileMenuOpen}
                        >
                            <span className="burger-line"></span>
                            <span className="burger-line"></span>
                        </button>

                        {!usarMenuLateral && (
                            <>
                                <div
                                    className={`menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                />

                                <div className={`menu-responsive ${isMobileMenuOpen ? 'open' : ''}`}>
                                    <Link to="/" className='logo-link' onClick={handleLinkClick}>
                                        <img className='lateral-logo' src='/images/logoadhoc.svg' alt='Logo ADHOC'/>
                                    </Link>

                                    <div className="user-actions">
                                        {authenticated ? (
                                            <>
                                                {renderMobileNav()}

                                                {/* Logout al fondo */}
                                                <div className="menu-lateral-acciones">
                                                    <button className='logout' onClick={handleLogout}>
                                                        <FiLogOut />
                                                        <p>{t('menu.salir')}</p>
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="menu-lateral-idioma"><SelectorIdioma variante="campo" /></div>
                                                <Link to="/iniciar-sesion" className='login' onClick={handleLinkClick}>{t('menu.iniciarSesion')}</Link>
                                                <Link to="/registrarse" className='new-user' onClick={handleLinkClick}>{t('menu.crearCuenta')}</Link>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>

        {/* Mismo panel lateral que el dashboard, en versión solo-móvil.
            Fuera de <header> a propósito: su backdrop-filter crearía bloque
            contenedor y el position:fixed del panel quedaría anclado ahí. */}
        {usarMenuLateral && (
            <>
                <div
                    className={`sidebar-overlay ${isMobileMenuOpen ? 'open' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                />
                <MenuLateral
                    open={isMobileMenuOpen}
                    onClose={() => setIsMobileMenuOpen(false)}
                    soloMovil
                />
            </>
        )}
        </>
    );
};

export default Header;
